import React from 'react'
// libs
import { useSnackbar } from 'notistack'
import { useDropzone } from 'react-dropzone'
// icons
import { AsteriskIcon } from 'components/FormElements/components/InputLabel'
// types
import { Field } from 'redux-form'
import { AttachmentType } from '../types'
import { WrappedFieldProps } from 'redux-form/lib/Field'
import { AssetType, IField } from 'types/graphql'
// components
import DocLinkField from '../components/DocLinkField'
// hooks
import { useFileUpload } from 'hooks/useFileUpload'
import { useDocLinkValue } from '../useDocLinkValue'
// views
import { Label, Wrapper } from './views'
// utils
import { DROPZONE_ACCEPT, getRejectionMessage } from '../utils'
import { registerPendingUpload, clearPendingUpload } from 'utils/pendingFileUploads'

interface Props extends WrappedFieldProps {
  field: IField
  required: boolean
  isDisabled?: boolean
}

const DocumentOrLink: React.FC<Props> = ({ field, required, input, meta, isDisabled }) => {
  const form = meta.form

  const { enqueueSnackbar } = useSnackbar()

  const { upload } = useFileUpload()

  const uploadRef = React.useRef(upload)
  const fileRef = React.useRef<File | null>(null)

  React.useEffect(() => {
    uploadRef.current = upload
  }, [upload])

  const clearExtra = () => {
    clearPendingUpload(form, input.name)
    fileRef.current = null
  }

  const { attachment, setValue, clearField, addLink } = useDocLinkValue({
    input,
    form,
    onAfterClear: clearExtra,
    onAfterLinkSet: clearExtra
  })

  const processFile = (file?: File) => {
    if (!file) {
      return
    }

    fileRef.current = file

    setValue({ name: file.name, size: file.size, url: '', type: AttachmentType.Image, _pending: true })

    registerPendingUpload(form, input.name, async () => {
      const currentFile = fileRef.current

      if (!currentFile) return

      const isMsg = currentFile.type === 'application/vnd.ms-outlook' || currentFile.name.toLowerCase().endsWith('.msg')
      const contentType = isMsg ? 'application/vnd.ms-outlook' : currentFile.type
      const url = await uploadRef.current(currentFile, {
        file: {
          assetType: AssetType.ActivityFile,
          contentType,
          entityPrefix: `entity/${field.id}/assets`,
          originalFileName: currentFile.name
        }
      })
      setValue({ name: currentFile.name, size: currentFile.size, url, type: AttachmentType.Image })
      fileRef.current = null
    })
  }

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: ([file]) => processFile(file),
    onDropRejected: (rejections) => enqueueSnackbar(getRejectionMessage(rejections), { variant: 'error' }),
    noClick: true,
    multiple: false,
    disabled: isDisabled,
    // prevents the outer withDropzone from also catching the drop (nested dropzones)
    noDragEventsBubbling: true,
    accept: DROPZONE_ACCEPT
  })

  return (
    <Wrapper>
      <Label>
        {required && <AsteriskIcon />} {field.label}
      </Label>
      <DocLinkField
        disabled={isDisabled}
        attachment={attachment}
        isDragActive={isDragActive}
        getRootProps={getRootProps}
        getInputProps={getInputProps}
        onBrowse={open}
        onClear={clearField}
        onAddLink={addLink}
      />
    </Wrapper>
  )
}

const FieldComponent = (props) => <Field {...props} component={DocumentOrLink} />

export default FieldComponent
